// A heading or line keyed the same in both views of a document, its edges
// measured from the scrolling container's top.
export type ScrollAnchorItem = { key: string; top: number; bottom: number };

export type ScrollAnchor = {
  // The anchor and every item above it, nearest first: a line the other view
  // hides gives way to the closest one above it.
  items: { key: string; offset: number }[];
  ratio: number;
};

export type ScrollMetrics = {
  scrollTop: number;
  scrollHeight: number;
  clientHeight: number;
};

const getScrollRange = ({ scrollHeight, clientHeight }: ScrollMetrics) =>
  Math.max(0, scrollHeight - clientHeight);

// `readingTop` is where the container's view starts under what sticks over
// it, as a document's toolbar does.
export const takeScrollAnchor = ({
  items,
  scroll,
  readingTop,
}: {
  items: ScrollAnchorItem[];
  scroll: ScrollMetrics;
  readingTop: number;
}): ScrollAnchor => {
  const anchorIndex = items.findIndex(({ bottom }) => bottom > readingTop);
  const range = getScrollRange(scroll);
  return {
    items: items
      .slice(0, anchorIndex + 1)
      .reverse()
      .map(({ key, top }) => ({ key, offset: top })),
    ratio: range > 0 ? scroll.scrollTop / range : 0,
  };
};

// The scroll of the container that puts the anchor, or the nearest item
// above it that this view shows, back at its offset.
export const placeScrollAnchor = ({
  anchor,
  items,
  scroll,
}: {
  anchor: ScrollAnchor;
  items: ScrollAnchorItem[];
  scroll: ScrollMetrics;
}): number => {
  const range = getScrollRange(scroll);
  const kept = anchor.items
    .map(({ key, offset }) => ({
      offset,
      item: items.find((candidate) => candidate.key === key),
    }))
    .find(({ item }) => item);
  const target = kept?.item
    ? scroll.scrollTop + kept.item.top - kept.offset
    : anchor.ratio * range;
  return Math.min(Math.max(target, 0), range);
};
